<template>
  <div>
    <Toast />
    <div class="flex items-center w-full p-3 my-3 bg-white rounded-lg shadow-sm">
      <router-link to="/vendor" class="text-lg font-medium text-gray-900 hover:text-blue-600">
        Publisher
      </router-link>
      <div class="px-3 text-gray-400">
        &bull;
      </div>
      <router-link :to="`/vendor/edit/${$route.params.id}?tab=2`" class="text-lg font-medium text-gray-900 hover:text-blue-600">
        Publisher Agreement
      </router-link>
    </div>

    <div class="flex-col w-full mb-3 bg-white rounded-lg">
      <div class="flex items-center justify-between w-full px-4 py-3 mt-3 rounded-lg ">
        <div class="flex flex-col w-1/2">
          <FormLabel htmlFor="currency">
            Payment Currency
          </FormLabel>

          <SelectRadio
            id="currency"
            defaultText="Select Payment Currency"
            v-model="form.vACurrency"
            :options="currencyOptions"
            :isError="isVACurrencyError"
          />

          <p v-if="isVACurrencyError" class="mt-1 text-sm text-red-500">Must be filled.</p>

          <FormLabel htmlFor="revenue" class="mt-3">
            Revenue Share
          </FormLabel>

          <multiselect
            v-model="form.vARevenue"
            label="name"
            track-by="name"
            placeholder="type to find name of revenue share..."
            open-direction="bottom"
            class="box-border"
            :options="revenueItems"
            :searchable="true"
            :loading="isLoadingSearchRevenue"
            :close-on-select="true"
            @search-change="findRevenue"
            @select="selectRevenue"
          >
            <span slot="noResult">
              Oops! No elements found. Consider changing the search query.
            </span>
          </multiselect>

          <p v-if="isVARevenueError" class="mt-1 text-sm text-red-500">Must be filled.</p>

          <DatetimeCustom
            :timeOnlyHours="true"
            class="mt-3"
            :label="'Start Date'"
            v-model="form.vAStartDate"
          />

          <p v-if="isVAStartDateError" class="mt-1 text-sm text-red-500">Must be filled.</p>

          <DatetimeCustom
            :timeOnlyHours="true"
            class="mt-3"
            :label="'End Date'"
            v-model="form.vAEndDate"
          />

          <p v-if="isVAEndDateError" class="mt-1 text-sm text-red-500">Must be filled.</p>
          
          <p v-if="isDateRangeError" class="mt-1 text-sm text-red-500">End date must be greater than start date.</p>

          <FormLabel htmlFor="status" className="mt-3">
            Status
          </FormLabel>

          <GmSwitch id="status" v-model="form.vAIsActive"/>

          <FormLabel className="mt-3" disabled>
            Default Fixed Amount
          </FormLabel>

          <TextInput
            type="text"
            id="defaultFixed"
            class="text-gray-400"
            disabled
            :value="defaultFixed || '-'"
          />

          <FormLabel className="mt-3" disabled>
            Default Percentage Amount
          </FormLabel>

          <TextInput
            type="text"
            id="defaultPercentage"
            class="text-gray-400"
            disabled
            :value="defaultPercentage || '-'"
          />

          <div class="flex gap-5 mt-4">
            <Button @click="handleSave">
              Save
            </Button>

            <Button
              variant="outlined"
              color="alternative"
              @click="handleCancel"
            >
              Cancel
            </Button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment';

import FormLabel from '@/components/ui/FormLabel';
import DatetimeCustom from '@/components/form/Datetime';
import Button from '@/components/ui/Button';
import SelectRadio from '@/components/ui/SelectRadio';
import TextInput from '@/components/ui/TextInput';
import GmSwitch from '@/components/ui/Switch';
import Toast from '@/components/form/Toast';
import { EventBus } from '@/event-bus';

import constant from '@/store/constant';


const paymentCurrencies = Object.entries(constant.PUBLISHER_PAYMENT_CURRENCY).map(
  (val) => ({
    text: val[0],
    value: val[1],
  }),
);

export default {
  name: 'VendorNewEditAgreement',
  components: {
    FormLabel,
    DatetimeCustom,
    Button,
    SelectRadio,
    TextInput,
    GmSwitch,
    Toast
  },
  data() {
    return {
      form: {
        vACurrency: null,
        vARevenue: [],
        vAStartDate: moment().toString(),
        vAEndDate: moment().toString(),
        vAIsActive: false,
      },

      vendor: [],

      isVACurrencyError: false,
      isVARevenueError: false,
      isVAStartDateError: false,
      isVAEndDateError: false,
      isDateRangeError: false,

      currencyOptions: paymentCurrencies,

      isLoadingSearchRevenue: false,
      revenueItems: [],
      defaultFixed: null,
      defaultPercentage: null,
      isLoadingCreateVendor: false,
    }
  },
  computed: {
    ...mapState({
      itemsRevenueShares: ({ revenueshares }) => revenueshares.items,
      item: ({ vendor_revenue }) => vendor_revenue.item,
      isLoading: ({ vendor_revenue }) => vendor_revenue.isLoading,
      successMessage: ({ vendor_revenue }) => vendor_revenue.successMessage,
      errorMessage: ({ vendor_revenue }) => vendor_revenue.errorMessage
    })
  },
  mounted() {
    this.fetchVendorAggrementById({ id: this.$route.params.agreement_id });
    this.actionGetRevenueShares();
  },
  watch: {
    item() {
      if (!Object.keys(this.item).length) return;
      this.getVendorRevenue();
    },
    successMessage: function() {
      if (!this.successMessage) return;
      this.alertMsg(this.successMessage, 'success');
      this.$router.push(`/vendor/edit/${this.$route.params.id}?tab=2`);
    },
    errorMessage: function() {
      if (!this.errorMessage) return;
      this.alertMsg(this.errorMessage, 'error');
    },
    'form.vAStartDate': function() {
      if (this.form.vAStartDate && this.form.vAEndDate) {
        this.validateDateRange();
      }
    },
    'form.vAEndDate': function() {
      if (this.form.vAStartDate && this.form.vAEndDate) {
        this.validateDateRange();
      }
    },
  },
  methods: {
    ...mapActions('organization', ['searchOrganization']),
    ...mapActions('revenueshares', ['fetchAllRevenueShares']),
    ...mapActions('vendor_revenue', [
      'findVendorRevenueByID',
      'findVendorRevenue',
      'editVendorRevenue',
      'fetchVendorAggrementById'
    ]),

    getVendorRevenue() {
      const data = this.item;
      this.vendor = data.vendor;
      this.form.vARevenue = data.revenue_share;
      this.form.vAEndDate = data.valid_to;
      this.form.vACurrency = data.vendor.payment_currency
      this.form.vAStartDate = moment(data.valid_from).format('YYYY-MM-DD');
      this.form.vAIsActive = data.is_active;
      this.defaultFixed = data.revenue_share.default_fixed_amount;
      this.defaultPercentage = data.revenue_share.default_percentage_amount;
    },

    findRevenue(keyword) {
      if (keyword.length > 2) {
        this.isLoadingSearchRevenue = true;
        const payload = {
          q: keyword,
        };
        this.findVendorRevenue(payload)
          .then((resp) => {
            this.revenueItems = resp.data.data;
            this.isLoadingSearchRevenue = false;
          })
          .catch((err) => {
            this.isLoadingSearchRevenue = false;
            console.log(err);
          });
      }
    },

    selectRevenue(data) {
      this.findVendorRevenueByID(data).then((res) => {
        const { default_fixed_amount, default_percentage_amount } = res.data.data;
        this.defaultFixed = default_fixed_amount;
        this.defaultPercentage = default_percentage_amount;
      });
    },

    validateDateRange() {
      this.isDateRangeError = false;
      
      if (this.form.vAStartDate && this.form.vAEndDate) {
        const startDate = moment(this.form.vAStartDate);
        const endDate = moment(this.form.vAEndDate);
        
        if (startDate.isSameOrAfter(endDate)) {
          this.isDateRangeError = true;
          return false;
        }
      }
      
      return true;
    },

    handleSave() {
      // Validate date range before saving
      if (!this.validateDateRange()) {
        return;
      }

      const payload = {
        id: this.$route.params.agreement_id,
        vendor_id: this.$route.params.id,
        revenue_id: this.form.vARevenue.id,
        valid_from: this.form.vAStartDate,
        valid_to: this.form.vAEndDate,
        is_active: this.form.vAIsActive,
        payment_currency: this.form.vACurrency
      }

      this.editVendorRevenue(payload)
    },

    handleCancel() {
      this.$router.replace(`/vendor/edit/${this.$route.params.id}?tab=2`)
    },

    actionGetRevenueShares() {
      const payload = {
        status: true,
      };
      this.fetchAllRevenueShares(payload);
    },

    alertMsg(msg, type) {
      EventBus.$emit('show-toast', {
        message: msg,
        type: type,
      });
    },
  }
}
</script>
